import React from 'react';
import { StandardSearchBar, StandardSelect } from '../standard';

/**
 * Shared header for Credit Block list pages (Clients, Credit Blocks, Cases).
 *
 * Replaces three slightly-different headers in CreditBlockApp.tsx with a
 * single consistent layout:
 *
 *   ┌──────────────────────────────────────────────────────────────────────┐
 *   │ [Title or List View Selector]      [Count]   [Primary Action Slot]  │
 *   │ [Search bar                                ] [Filters Slot]          │
 *   └──────────────────────────────────────────────────────────────────────┘
 *
 * Bottom row only renders when `search` or `filters` is provided.
 * Count formats automatically: "X of Y label" or "X label" (singular/plural
 * by `label`/`labelPlural`).
 */

interface ListViewOption {
  value: string;
  label: string;
}

interface CreditBlockListHeaderProps {
  /** Static title shown on the left. Mutually exclusive with `listView`. */
  title?: string;
  /** List-view selector dropdown (Clients tab uses this in place of title). */
  listView?: {
    value: string;
    options: ListViewOption[];
    onChange: (value: string) => void;
    ariaLabel?: string;
  };
  /** Count display under/next to title. Auto-formats "X label" or "X of Y label" */
  count?: {
    displayed: number;
    total?: number;
    label: string;
    labelPlural?: string;
    loading?: boolean;
    /** Extra text shown after the count, e.g. "(this page: 10)". */
    suffix?: React.ReactNode;
  };
  /** Right-side action button(s) for the top row, e.g. New Case. */
  primaryAction?: React.ReactNode;
  /** Bottom-row search bar. */
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
  };
  /** Bottom-row filter slot, rendered to the right of the search bar. */
  filters?: React.ReactNode;
}

const HEADER_CARD = 'bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700';

export const CreditBlockListHeader: React.FC<CreditBlockListHeaderProps> = ({
  title,
  listView,
  count,
  primaryAction,
  search,
  filters,
}) => {
  const hasBottomRow = Boolean(search || filters);

  return (
    <div className={HEADER_CARD}>
      <div className="px-6 py-5">
        {/* Top row: title or list-view, count, primary action */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {listView ? (
              <StandardSelect
                aria-label={listView.ariaLabel ?? 'List view'}
                value={listView.value}
                onChange={(e) => listView.onChange(e.target.value)}
                options={listView.options}
              />
            ) : title ? (
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                {title}
              </h3>
            ) : null}

            {count ? (
              <CountDisplay
                displayed={count.displayed}
                total={count.total}
                label={count.label}
                labelPlural={count.labelPlural}
                loading={count.loading}
                suffix={count.suffix}
              />
            ) : null}
          </div>

          {primaryAction ? (
            <div className="flex items-center gap-2 shrink-0">{primaryAction}</div>
          ) : null}
        </div>

        {/* Bottom row: search + filters */}
        {hasBottomRow ? (
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            {search ? (
              <div className="flex-1">
                <StandardSearchBar
                  value={search.value}
                  onChange={search.onChange}
                  placeholder={search.placeholder}
                />
              </div>
            ) : null}
            {filters ? <div className="flex items-center gap-2">{filters}</div> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

interface CountDisplayProps {
  displayed: number;
  total?: number;
  label: string;
  labelPlural?: string;
  loading?: boolean;
  suffix?: React.ReactNode;
}

const CountDisplay: React.FC<CountDisplayProps> = ({
  displayed,
  total,
  label,
  labelPlural,
  loading,
  suffix,
}) => {
  if (loading) {
    return <span className="text-sm text-gray-400 dark:text-gray-500">…</span>;
  }

  const labelText =
    displayed === 1
      ? label
      : labelPlural ?? `${label}s`;

  const showRange = typeof total === 'number' && total > 0 && total > displayed;

  return (
    <span className="text-sm tabular-nums whitespace-nowrap text-gray-600 dark:text-gray-400">
      {showRange
        ? `${displayed.toLocaleString()} of ${total.toLocaleString()} ${labelText}`
        : `${displayed.toLocaleString()} ${labelText}`}
      {suffix ? <span className="text-gray-500 dark:text-gray-500"> {suffix}</span> : null}
    </span>
  );
};

export default CreditBlockListHeader;
